import React, { useMemo, useState } from 'react';
import { useBillingPlans, useSubscription } from '../../hooks/useSubscription';
import {
  BILLING_INTERVAL_OPTIONS,
  BillingIntervalOption,
  BUNDLE_OPTIONS,
  formatCents,
  formatCurrency,
} from '../../utils/bundles';
import { BillingInterval, BillingPlan } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';

/**
 * Reusable plan/cadence picker: interval (Monthly / 3-Month / 6-Month / Yearly)
 * + optional multi-year bundle, a live summary, and a checkout button. Used on
 * the post-registration payment page AND inline on the dashboard's Plan &
 * billing tab (so both Free upgrades and Pro plan changes share one flow).
 *
 * The catalog is fetched from /api/subscription/plans so prices/discounts
 * always match what the backend will actually charge.
 */
export function PlanPicker({
  heading = 'Choose Your Plan',
  note = 'Pay monthly for flexibility, or save up to 44% per month with the yearly plan.',
  ctaLabel,
  mode = 'checkout',
}: {
  heading?: string;
  note?: string;
  ctaLabel?: string;
  /** 'checkout' = new subscription (redirect to Stripe). 'change' = modify the
   *  existing subscription in place (no redirect, no new trial, no duplicate). */
  mode?: 'checkout' | 'change';
}) {
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>('year');
  const [selectedBundleYears, setSelectedBundleYears] = useState(1);
  const [changeMsg, setChangeMsg] = useState<string | null>(null);
  const { createCheckout, isCreatingCheckout, changePlan, isChangingPlan } = useSubscription();
  const { data: plansResponse, isLoading: plansLoading } = useBillingPlans();
  const busy = isCreatingCheckout || isChangingPlan;
  const cta = ctaLabel ?? (mode === 'change' ? 'Update plan' : 'Continue to Payment');

  const plans = plansResponse?.plans;

  const planById = useMemo(() => {
    const map: Partial<Record<BillingInterval, BillingPlan>> = {};
    if (plans) {
      for (const plan of plans) {
        map[plan.id] = plan;
      }
    }
    return map;
  }, [plans]);

  const selectedPlan = planById[selectedInterval];
  const isYearly = selectedInterval === 'year';
  const yearBundles = useMemo(() => selectedPlan?.year_bundles ?? [], [selectedPlan]);

  const summary = useMemo(() => {
    if (isYearly) {
      const bundle = yearBundles.find((b) => b.bundle_years === selectedBundleYears);
      if (bundle) {
        return {
          label: selectedBundleYears === 1 ? '1 Year (Yearly plan)' : `${selectedBundleYears} Years bundle`,
          totalDisplay: formatCents(bundle.amount_cents),
          perYearDisplay: selectedBundleYears > 1 ? formatCents(bundle.price_per_year_cents) : null,
          discountPercent: bundle.discount_percent,
        };
      }
      const fallback = BUNDLE_OPTIONS.find((b) => b.years === selectedBundleYears);
      return {
        label: fallback?.label ?? 'Yearly',
        totalDisplay: formatCurrency(fallback?.totalPrice ?? 10),
        perYearDisplay: fallback && fallback.years > 1 ? formatCurrency(fallback.pricePerYear) : null,
        discountPercent: fallback?.discount ?? 0,
      };
    }

    if (selectedPlan) {
      return {
        label: selectedPlan.name,
        totalDisplay: formatCents(selectedPlan.amount_cents),
        perYearDisplay: null,
        discountPercent: 0,
      };
    }

    const fallback = BILLING_INTERVAL_OPTIONS.find((o) => o.id === selectedInterval);
    return {
      label: fallback?.label ?? 'Subscription',
      totalDisplay: fallback?.defaultPriceDisplay ?? '',
      perYearDisplay: null,
      discountPercent: 0,
    };
  }, [isYearly, selectedBundleYears, selectedInterval, selectedPlan, yearBundles]);

  const handleContinue = async () => {
    const payload = {
      billing_interval: selectedInterval,
      bundle_years: isYearly ? selectedBundleYears : 1,
    };
    if (mode === 'change') {
      setChangeMsg(null);
      try {
        const res = await changePlan(payload);
        setChangeMsg(res?.unchanged ? "You're already on that plan." : '✓ Your plan was updated.');
      } catch (e: any) {
        setChangeMsg(e?.response?.data?.detail || 'Could not update your plan. Please try again.');
      }
    } else {
      createCheckout(payload);
    }
  };

  const renderIntervalCard = (option: BillingIntervalOption) => {
    const plan = planById[option.id];
    const priceDisplay = plan ? formatCents(plan.amount_cents) : option.defaultPriceDisplay;
    const monthlyEquivalent = plan ? buildMonthlyEquivalent(plan) : option.defaultMonthlyEquivalent;
    const isSelected = selectedInterval === option.id;
    return (
      <button
        key={option.id}
        type="button"
        onClick={() => {
          setSelectedInterval(option.id);
          if (option.id !== 'year') setSelectedBundleYears(1);
        }}
        aria-pressed={isSelected}
        className="auth-panel"
        style={{
          position: 'relative', marginTop: 0, padding: '16px', textAlign: 'left', cursor: 'pointer',
          borderColor: isSelected ? 'var(--champ)' : undefined,
          background: isSelected ? 'rgba(216,189,130,.06)' : undefined,
        }}
      >
        {option.badge && (
          <span className="auth-badge" style={{ position: 'absolute', top: 10, right: 10 }}>{option.badge}</span>
        )}
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ivory)' }}>{option.label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--champ)', marginTop: 4 }}>{priceDisplay}</div>
        <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 4 }}>{monthlyEquivalent}</div>
      </button>
    );
  };

  return (
    <div className="auth-panel">
      <h2 style={{ marginBottom: 4 }}>{heading}</h2>
      <p className="auth-note" style={{ textAlign: 'left', marginTop: 0, marginBottom: 16 }}>{note}</p>

      {plansLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
          <LoadingSpinner size="md" />
        </div>
      )}

      {!plansLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          {BILLING_INTERVAL_OPTIONS.map(renderIntervalCard)}
        </div>
      )}

      {isYearly && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>Lock in a longer term?</h3>
          <p className="auth-note" style={{ textAlign: 'left', marginTop: 0, marginBottom: 16 }}>
            Pay for multiple years up front and save even more.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {BUNDLE_OPTIONS.map((bundle) => {
              const live = yearBundles.find((b) => b.bundle_years === bundle.years);
              const totalDisplay = live ? formatCents(live.amount_cents) : formatCurrency(bundle.totalPrice);
              const isSelected = selectedBundleYears === bundle.years;
              return (
                <button
                  key={bundle.years}
                  type="button"
                  onClick={() => setSelectedBundleYears(bundle.years)}
                  aria-pressed={isSelected}
                  className="auth-panel"
                  style={{
                    position: 'relative', marginTop: 0, padding: '12px', textAlign: 'left', cursor: 'pointer',
                    borderColor: isSelected ? 'var(--champ)' : undefined,
                    background: isSelected ? 'rgba(216,189,130,.06)' : undefined,
                  }}
                >
                  {bundle.badge && (
                    <span className="auth-badge" style={{ position: 'absolute', top: 8, right: 8 }}>{bundle.badge}</span>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ivory)' }}>{bundle.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--champ)', marginTop: 4 }}>{totalDisplay}</div>
                  {bundle.years > 1 && (
                    <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>
                      {live ? `${formatCents(live.price_per_year_cents)}/yr` : `${formatCurrency(bundle.pricePerYear)}/yr`}
                    </div>
                  )}
                  {bundle.discount > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--teal-bright)', marginTop: 2 }}>Save {bundle.discount}%</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="auth-panel" style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p className="auth-key" style={{ margin: 0 }}>Selected Plan</p>
            <p className="auth-val" style={{ fontSize: 18, margin: '2px 0 0' }}>
              {summary.label} — {summary.totalDisplay}
            </p>
            {summary.perYearDisplay && (
              <p style={{ fontSize: 12, color: 'var(--faint)', margin: '2px 0 0' }}>That's {summary.perYearDisplay}/yr</p>
            )}
          </div>
          <button
            onClick={handleContinue}
            disabled={busy}
            className="auth-btn"
            style={{ width: 'auto', padding: '0 32px' }}
          >
            {busy ? (
              <>
                <LoadingSpinner size="sm" />
                <span style={{ marginLeft: 8 }}>Processing...</span>
              </>
            ) : (
              cta
            )}
          </button>
        </div>
        {changeMsg && (
          <p className="auth-note" style={{ textAlign: 'left', margin: '12px 0 0' }}>{changeMsg}</p>
        )}
      </div>
    </div>
  );
}

function buildMonthlyEquivalent(plan: BillingPlan): string {
  const months = plan.stripe_interval === 'year' ? plan.stripe_interval_count * 12 : plan.stripe_interval_count;
  if (months <= 0) return '';
  const perMonthCents = plan.amount_cents / months;
  return `${formatCents(perMonthCents)}/mo equivalent`;
}
